<template>
  <div class="tw-relative tw-w-full">
    <t-flag-debug-banner v-if="isFlagDebugBannerAvailable" />
    <router-view />
    <vue3-progress-bar />
    <t-toasts />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import TToasts from "@/components/TToasts";
import TFlagDebugBanner from "@/components/TFlagDebugBanner";
import { useTheme } from "@/composables";
import { useFlag } from "@/composables/useFlags/useFlag";
import { useHarnessFlagsBootstrap } from "@/composables/useFlags/useHarnessFlagsBootstrap";
import { setupDayjsLocale } from "@/plugins/i18n-dayjs";

type AuthUser = {
  accountname: string;
  email: string;
  id: string;
  twoFactorAuthEnabled?: boolean;
};

const authStore = useAuthenticationStore();
const router = useRouter();
const i18n = useI18n();

useTheme();
// Initialise Harness feature flags (no-op unless the harness source is active).
useHarnessFlagsBootstrap();

const { isFeatureAvailable: isFlagDebugBannerAvailable } =
  useFlag("HARNESS_DEMO_FLAG");
// eslint-disable-next-line @typescript-eslint/no-explicit-any
setupDayjsLocale(i18n as any);

const authUser = computed<AuthUser | null>(
  () => authStore.authUser as AuthUser | null,
);

onMounted(async () => {
  await router.isReady();

  const route = router.currentRoute.value;
  const path = route?.path || "/";

  if (
    !authUser.value &&
    !path.includes("/sso/callback") &&
    !path.includes("/azure/callback") &&
    !path.includes("auth/password/set") &&
    !path.includes("/auth/sign-in")
  ) {
    localStorage.clear();

    const queryParams: Record<string, string> = {};
    const currentFullPathForRedirect = route?.fullPath || path;
    if (
      currentFullPathForRedirect &&
      currentFullPathForRedirect !== "/" &&
      currentFullPathForRedirect.trim() !== ""
    ) {
      queryParams.redirect = currentFullPathForRedirect;
    }

    router.push({
      name: "auth-sign-in",
      query: queryParams,
    });
  }
});
</script>
